import type { ReactNode } from 'react';
import { classNames } from '~/utils/styles';

export interface DetailTableColumn {
  key: string;
  header: ReactNode;
  /** Fixed column width (any CSS width, e.g. "12%" or "80px"), applied via a shared <colgroup>. */
  width?: string;
  align?: 'left' | 'right';
}

interface DetailTableProps {
  columns: DetailTableColumn[];
  children: ReactNode;
  /**
   * When set (px), the body scrolls under a header row that sticks to the top of the scroll area.
   * Header and body stay in one table so their column widths can never drift apart.
   */
  maxBodyHeight?: number;
}

const ColGroup = ({ columns }: { columns: DetailTableColumn[] }) => (
  <colgroup>
    {columns.map((col) => (
      <col key={col.key} style={col.width ? { width: col.width } : undefined} />
    ))}
  </colgroup>
);

/**
 * Background and bottom border sit on the `th` rather than the `thead` so they travel with the cells
 * when `isSticky` lifts them out of normal flow — a border on `thead` would be left behind on scroll.
 */
const Header = ({ columns, isSticky }: { columns: DetailTableColumn[]; isSticky: boolean }) => (
  <thead>
    <tr>
      {columns.map((col) => (
        <th
          key={col.key}
          className={classNames(
            'border-b border-neutral-400 bg-neutral-100 px-3 py-[14px] text-[13px] font-bold text-neutral-1000',
            col.align === 'right' ? 'text-right' : 'text-left',
            isSticky && 'sticky top-0 z-10',
          )}
        >
          {col.header}
        </th>
      ))}
    </tr>
  </thead>
);

export const DetailTable = ({ columns, children, maxBodyHeight }: DetailTableProps) => {
  const isScrollable = maxBodyHeight != null;

  return (
    <div
      className={classNames('w-full border border-neutral-400', isScrollable ? 'overflow-y-auto' : 'overflow-hidden')}
      style={isScrollable ? { maxHeight: maxBodyHeight } : undefined}
    >
      <table className="w-full table-fixed">
        {columns.some((col) => col.width != null) ? <ColGroup columns={columns} /> : null}
        <Header columns={columns} isSticky={isScrollable} />
        <tbody>{children}</tbody>
      </table>
    </div>
  );
};

interface DetailTableCellProps {
  children: ReactNode;
  className?: string;
  colSpan?: number;
}

export const DetailTableCell = ({ children, className, colSpan }: DetailTableCellProps) => {
  return (
    <td className={classNames('px-3 py-[11px] text-sm text-neutral-1000', className)} colSpan={colSpan}>
      {children}
    </td>
  );
};
